import { Button } from "@stockline/ui/components/button";
import { Input } from "@stockline/ui/components/input";
import { Textarea } from "@stockline/ui/components/textarea";
import {
  formatZoned,
  useCompanyTimezone,
} from "@/lib/datetime/companyTimezone";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "@/styles/scrollbar.css";

/**
 * Section-based markdown editor.
 *
 * Every block is a *section*: a title + a body + two timestamps
 * (`createdAt`, `updatedAt`). Users cannot type raw markdown directly;
 * they add a section and write inside it. On load, existing markdown
 * is parsed and metadata recovered from HTML comments left by previous
 * saves. Round-trip is stable for editor-authored content; anything
 * that doesn't fit the structure is rescued into a leading "general"
 * block flagged so the user can convert it.
 */

export type SectionKind = "general" | "section";

export type Section = {
  /** React key — never serialized; regenerated on every parse. */
  localId: string;
  kind: SectionKind;
  /** Heading text. Empty for "general". */
  title: string;
  /** Free-form body. Plain text; pre-existing markdown is preserved. */
  body: string;
  /** "YYYY-MM-DD HH:mm". Absent on general blocks and on legacy sections
   *  authored before timestamps were stored. */
  createdAt?: string;
  updatedAt?: string;
};

const LEGACY_TIMESTAMP_HEAD_RE = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/;
// Metadata comment shape: ` <!-- created="..." updated="..." -->`
const META_RE = /\s*<!--\s*created="([^"]*)"\s+updated="([^"]*)"\s*-->\s*$/;

/**
 * Wall-clock stamp in the company's configured timezone — so two viewers
 * in different timezones see the same "2026-05-15 14:30" string for the
 * same event, instead of each rendering their browser local clock. Per
 * the datetime convention (docs/DATETIME_CONVENTION.md), wall-clock
 * display is always company-tz, never the browser's tz.
 */
export function nowStamp(tz: string): string {
  return formatZoned(new Date().toISOString(), tz, "yyyy-MM-dd HH:mm");
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s-${Math.random().toString(36).slice(2)}`;
}

/** Extract optional metadata comment from a heading title. Returns the
 *  cleaned title plus parsed timestamps when present. */
function splitMetadata(rawTitle: string): {
  title: string;
  createdAt?: string;
  updatedAt?: string;
} {
  const m = META_RE.exec(rawTitle);
  if (!m) return { title: rawTitle.trim() };
  return {
    title: rawTitle.replace(META_RE, "").trim(),
    createdAt: m[1] || undefined,
    updatedAt: m[2] || undefined,
  };
}

/**
 * Parse a markdown string into structured sections.
 *
 * Rules:
 *   - `## Title` / `# Title` → section with that title
 *   - `### YYYY-MM-DD HH:mm` (legacy timestamped note) → migrates into a
 *     section with empty title; the timestamp is hoisted into createdAt
 *     and updatedAt so the user keeps the context.
 *   - `### other` → also a section
 *   - Any content before the first heading lands in a leading "general"
 *     block — recovery path for handwritten / pasted markdown.
 *   - Trailing `<!-- created="..." updated="..." -->` on the heading line
 *     restores timestamps from a previous round-trip.
 */
/** Match a markdown heading line and return the raw title plus whether
 *  it was h3 (which we treat specially for legacy timestamp notes). */
function matchHeading(
  line: string,
): { rawTitle: string; isH3: boolean } | null {
  const h3 = /^###\s+(.+?)\s*$/.exec(line);
  if (h3) return { rawTitle: h3[1], isH3: true };
  const h2 = /^##\s+(.+?)\s*$/.exec(line);
  if (h2) return { rawTitle: h2[1], isH3: false };
  const h1 = /^#\s+(.+?)\s*$/.exec(line);
  if (h1) return { rawTitle: h1[1], isH3: false };
  return null;
}

/** Construct a fresh `section` block from a heading match, hoisting any
 *  legacy `### YYYY-MM-DD HH:mm` timestamp into createdAt/updatedAt. */
function startSection(rawTitle: string, isH3: boolean): Section {
  const { title, createdAt, updatedAt } = splitMetadata(rawTitle);
  const isLegacyNote = isH3 && LEGACY_TIMESTAMP_HEAD_RE.test(title);
  return {
    localId: newId(),
    kind: "section",
    title: isLegacyNote ? "" : title,
    body: "",
    createdAt: createdAt ?? (isLegacyNote ? title : undefined),
    updatedAt: updatedAt ?? (isLegacyNote ? title : undefined),
  };
}

export function parseSections(markdown: string): Section[] {
  if (!markdown.trim()) return [];
  const out: Section[] = [];
  let current: Section | null = null;

  const flush = () => {
    if (!current) return;
    current.body = current.body.replace(/\n+$/g, "");
    if (current.kind === "general" && !current.body.trim()) {
      current = null;
      return;
    }
    out.push(current);
    current = null;
  };

  for (const line of markdown.split("\n")) {
    const heading = matchHeading(line);
    if (heading) {
      flush();
      current = startSection(heading.rawTitle, heading.isH3);
    } else {
      current ??= { localId: newId(), kind: "general", title: "", body: "" };
      current.body += (current.body ? "\n" : "") + line;
    }
  }
  flush();
  return out;
}

/** Inverse of parseSections — always emits a canonical, re-parseable form. */
export function serializeSections(sections: Section[]): string {
  const parts: string[] = [];
  for (const s of sections) {
    const body = s.body.replace(/\s+$/g, "");
    if (s.kind === "general") {
      if (body) parts.push(body);
      continue;
    }
    const title = s.title.trim();
    const meta =
      s.createdAt || s.updatedAt
        ? ` <!-- created="${s.createdAt ?? ""}" updated="${s.updatedAt ?? ""}" -->`
        : "";
    const head = `## ${title}${meta}`.replace(/\s+$/, "");
    parts.push(body ? `${head}\n\n${body}` : head);
  }
  return parts.join("\n\n");
}

interface SectionedEditorProps {
  value: string;
  onChange: (value: string) => void;
  isReadOnly?: boolean;
}

/** Imperative handle so the parent dialog can host its own "Add section"
 *  button (e.g. in the dialog header) without re-implementing the
 *  add-section logic or duplicating state. */
export interface SectionedEditorHandle {
  addSection: () => void;
}

export const SectionedEditor = forwardRef<
  SectionedEditorHandle,
  SectionedEditorProps
>(function SectionedEditor({ value, onChange, isReadOnly = false }, ref) {
  const { t } = useTranslation();
  const tz = useCompanyTimezone();
  // Parse once on mount — local section list is then authoritative until
  // an external value change (e.g. a save round-trip) overrides it.
  const [sections, setSections] = useState<Section[]>(() =>
    parseSections(value),
  );
  const lastEmittedRef = useRef<string>(value);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    setSections(parseSections(value));
    lastEmittedRef.current = value;
  }, [value]);

  const commit = useCallback(
    (next: Section[]) => {
      setSections(next);
      const serialized = serializeSections(next);
      lastEmittedRef.current = serialized;
      onChange(serialized);
    },
    [onChange],
  );

  // Ref mirror of `sections` so memoised callbacks can read the latest
  // list without depending on it (which would re-run them every keystroke
  // and break `useImperativeHandle`'s stability). Updaters passed to
  // `setSections` MUST stay pure — React may call them more than once
  // in strict / concurrent mode, which would emit duplicate `onChange`s.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const addSection = useCallback(() => {
    if (isReadOnly) return;
    const stamp = nowStamp(tz);
    const next: Section[] = [
      ...sectionsRef.current,
      {
        localId: newId(),
        kind: "section",
        title: "",
        body: "",
        createdAt: stamp,
        updatedAt: stamp,
      },
    ];
    commit(next);
  }, [isReadOnly, tz, commit]);

  // Expose addSection so the dialog header can render a single, compact
  // "+ Add section" trigger right under the close X — saves a whole row
  // of vertical real estate inside the editor.
  useImperativeHandle(ref, () => ({ addSection }), [addSection]);

  const updateSection = (id: string, patch: Partial<Section>) => {
    const stamp = nowStamp(tz);
    commit(
      sections.map((s) =>
        s.localId === id
          ? {
              ...s,
              ...patch,
              // Bump updatedAt whenever title or body changes on a real
              // section; general blocks stay un-dated.
              ...(s.kind === "section" &&
              (patch.title !== undefined || patch.body !== undefined)
                ? { updatedAt: stamp, createdAt: s.createdAt ?? stamp }
                : {}),
            }
          : s,
      ),
    );
  };

  const removeSection = (id: string) => {
    commit(sections.filter((s) => s.localId !== id));
  };

  const moveSection = (id: string, dir: -1 | 1) => {
    const idx = sections.findIndex((s) => s.localId === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sections.length) return;
    const next = sections.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    commit(next);
  };

  // Auto-number titled sections — visual aid only, never persisted.
  // General recovery blocks are skipped so the numbering stays clean.
  const numberedIndex = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const s of sections) {
      if (s.kind === "section") {
        n += 1;
        map.set(s.localId, n);
      }
    }
    return map;
  }, [sections]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* The "Add section" button is hosted by the parent dialog header
          (see ContextEditor) to compact the layout. The empty state still
          offers an inline CTA so an empty editor is actionable. */}

      {sections.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
          <span>
            {isReadOnly
              ? t("common.aiManagement.sections.emptyReadOnly")
              : t("common.aiManagement.sections.empty")}
          </span>
          {!isReadOnly && (
            <Button
              size="sm"
              variant="outline"
              onClick={addSection}
              data-testid="sectioned-editor-empty-add-section"
              className="h-8 gap-1.5 rounded-md"
            >
              <Plus className="h-3.5 w-3.5" />
              {t("common.aiManagement.sections.addSection")}
            </Button>
          )}
        </div>
      ) : (
        // Bordered scroll area — the top/bottom borders signal where the
        // scrollable region begins and ends so content doesn't visually
        // crop into the surrounding chrome. `persistent-scroll` keeps the
        // scrollbar visible even on browsers that auto-hide overlay bars,
        // so the user can tell there's more below without scrolling first.
        <div className="persistent-scroll flex flex-1 flex-col gap-2 overflow-y-auto border-y border-border bg-muted/5 px-1 py-2">
          {sections.map((s, idx) => (
            <SectionCard
              key={s.localId}
              section={s}
              displayNumber={numberedIndex.get(s.localId) ?? null}
              isFirst={idx === 0}
              isLast={idx === sections.length - 1}
              isReadOnly={isReadOnly}
              onTitleChange={(title) => updateSection(s.localId, { title })}
              onBodyChange={(body) => updateSection(s.localId, { body })}
              onMoveUp={() => moveSection(s.localId, -1)}
              onMoveDown={() => moveSection(s.localId, 1)}
              onRemove={() => removeSection(s.localId)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

interface SectionCardProps {
  readonly section: Section;
  readonly displayNumber: number | null;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isReadOnly: boolean;
  readonly onTitleChange: (title: string) => void;
  readonly onBodyChange: (body: string) => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onRemove: () => void;
}

function SectionCard({
  section,
  displayNumber,
  isFirst,
  isLast,
  isReadOnly,
  onTitleChange,
  onBodyChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: SectionCardProps) {
  const { t } = useTranslation();
  const isGeneral = section.kind === "general";

  return (
    <div className="rounded-md border border-border bg-background p-3 shadow-sm transition-colors">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-medium",
            !isGeneral && "bg-violet-500/10 text-violet-600",
            isGeneral && "bg-muted text-muted-foreground",
          )}
          title={
            isGeneral
              ? t("common.aiManagement.sections.kindGeneral")
              : t("common.aiManagement.sections.kindSection")
          }
        >
          {isGeneral ? "—" : (displayNumber ?? "")}
        </span>

        {isGeneral ? (
          <span className="min-w-0 flex-1 truncate text-xs italic text-muted-foreground">
            {t("common.aiManagement.sections.generalLabel")}
          </span>
        ) : (
          <>
            <Input
              value={section.title}
              onChange={(e) => onTitleChange(e.target.value)}
              readOnly={isReadOnly}
              data-testid="sectioned-editor-section-title"
              placeholder={t("common.aiManagement.sections.titlePlaceholder")}
              className="h-7 min-w-0 flex-1 border-0 bg-transparent px-1 text-sm font-medium shadow-none focus-visible:bg-muted focus-visible:ring-1 focus-visible:ring-primary/40"
            />
            {/* Inline stamp sits between the title and the row-end controls.
                `min-w-0` + `truncate` lets it crop instead of pushing the
                action buttons off the right edge on narrow widths. */}
            <SectionTimestamps section={section} className="hidden sm:flex" />
          </>
        )}

        {!isReadOnly && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst}
              aria-label={t("common.aiManagement.sections.moveUp")}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast}
              aria-label={t("common.aiManagement.sections.moveDown")}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label={t("common.aiManagement.sections.remove")}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Stamp shown inline with the title on ≥sm; falls back below the
          row on narrow widths so it never collides with the actions. */}
      {!isGeneral && (
        <SectionTimestamps section={section} className="mt-1 ml-8 sm:hidden" />
      )}

      <AutoGrowTextarea
        value={section.body}
        onChange={(e) => onBodyChange(e.target.value)}
        readOnly={isReadOnly}
        placeholder={t("common.aiManagement.sections.bodyPlaceholder")}
        className="mt-2 min-h-[72px] resize-none overflow-hidden border-0 bg-muted/30 text-sm focus-visible:bg-muted focus-visible:ring-1 focus-visible:ring-primary/40"
        data-testid="sectioned-editor-body"
      />
    </div>
  );
}

/**
 * createdAt / updatedAt indicator. Rendered inline next to the title on
 * wide layouts and below on narrow ones (the parent controls placement
 * via `className`). Truncates with ellipsis so it never pushes the
 * row-end controls off-screen on tight widths.
 */
function SectionTimestamps({
  section,
  className,
}: Readonly<{
  section: Section;
  className?: string;
}>) {
  const { t } = useTranslation();
  if (!section.createdAt && !section.updatedAt) return null;
  const created = section.createdAt;
  const updated = section.updatedAt;
  const sameOrNoUpdate = !updated || updated === created;
  return (
    <span
      className={cn(
        "shrink min-w-0 max-w-[260px] truncate text-[11px] text-muted-foreground tabular-nums",
        className,
      )}
      title={
        created && updated && !sameOrNoUpdate
          ? `${created} → ${updated}`
          : (created ?? updated ?? "")
      }
    >
      {created &&
        t("common.aiManagement.sections.createdAt", { stamp: created })}
      {!sameOrNoUpdate && (
        <>
          <span className="mx-1 opacity-50">·</span>
          {t("common.aiManagement.sections.updatedAt", { stamp: updated })}
        </>
      )}
    </span>
  );
}

/**
 * Textarea that grows with its content — no internal scrollbar. The outer
 * section list is the only scroll surface, so users can scan all sections
 * in one continuous flow.
 */
function AutoGrowTextarea(
  props: Readonly<React.TextareaHTMLAttributes<HTMLTextAreaElement>>,
) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [props.value]);
  return <Textarea ref={ref} {...props} />;
}
